import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import store from './store';
import { loadUser } from './store/authSlice';
import { AppKitProvider } from '@reown/appkit/react';
import { projectId, networks, metadata, ethersAdapter } from './contexts/WalletContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import Landing from './pages/Landing';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Explore from './pages/Explore';
import ContentView from './pages/ContentView';
import Wallet from './pages/Wallet';
import CreatorPanel from './pages/CreatorPanel';
import UploadContent from './pages/UploadContent';
import CreatorProfile from './pages/CreatorProfile';
import EditProfile from './pages/EditProfile';
import DepositGuide from './pages/DepositGuide';
import Web3Guide from './pages/Web3Guide';
import LearnFees from './pages/LearnFees';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailSent from './pages/VerifyEmailSent';
import Movies from './pages/Movies';
import Series from './pages/Series';
import Documentaries from './pages/Documentaries';
import Courses from './pages/Courses';
import Mentorships from './pages/Mentorships';
import Others from './pages/Others';
import Library from './pages/Library';
import Settings from './pages/Settings';
import './App.css';

function Layout() {
  const location = useLocation();
  const isLanding = location.pathname === '/';

  return (
    <>
      {!isLanding && <Navbar />}
      {isLanding ? (
        <Routes>
          <Route path="/" element={<Landing />} />
        </Routes>
      ) : (
        <div className="app-layout">
          <main className="main-content">
            <Routes>
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/movie" element={<Movies />} />
              <Route path="/series" element={<Series />} />
              <Route path="/documentary" element={<Documentaries />} />
              <Route path="/course" element={<Courses />} />
              <Route path="/mentorship" element={<Mentorships />} />
              <Route path="/other" element={<Others />} />
              <Route path="/content/:id" element={<ContentView />} />
              <Route path="/library" element={<PrivateRoute><Library /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
              <Route path="/wallet" element={<PrivateRoute><Wallet /></PrivateRoute>} />
              <Route path="/deposit-guide" element={<PrivateRoute><DepositGuide /></PrivateRoute>} />
              <Route path="/web3-guide" element={<Web3Guide />} />
              <Route path="/fees" element={<LearnFees />} />
              <Route path="/upload" element={<PrivateRoute><UploadContent /></PrivateRoute>} />
              <Route path="/creator" element={<PrivateRoute><CreatorPanel /></PrivateRoute>} />
              <Route path="/creator/edit-profile" element={<PrivateRoute><EditProfile /></PrivateRoute>} />
              <Route path="/creator/:username" element={<CreatorProfile />} />
              <Route path="/profile/:username" element={<CreatorProfile />} />
            </Routes>
          </main>
          <Footer />
        </div>
      )}
    </>
  );
}

function AppContent() {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadUser());
  }, [dispatch]);

  return (
    <BrowserRouter>
      <Layout />
    </BrowserRouter>
  );
}

function App() {
  return (
    <AppKitProvider
      projectId={projectId}
      networks={networks}
      metadata={metadata}
      adapters={[ethersAdapter]}
      themeMode="dark"
      themeVariables={{ '--w3m-accent': '#C8963E' }}
      features={{ email: false, socials: false, allWallets: true }}
      featuredWalletIds={[
        'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa', // Coinbase Wallet
        'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
        'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
        '0b415a746b9d66050aab93254901aae8e73b68e15e95ad07b85b20d0cf607ce8', // Brave Wallet
        '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // Trust Wallet
        '1ae92b26df02f0abca6304df07debccd18262fdf5fe82daa81593582dac9a369', // Rainbow
      ]}
    >
      <Provider store={store}>
        <AppContent />
      </Provider>
    </AppKitProvider>
  );
}

export default App;
